import { useState, useEffect, useCallback } from 'react';
import { aiCmaAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Robot, CheckCircle, XCircle, SpinnerGap, User, ChatText } from '@phosphor-icons/react';
import { toast } from 'sonner';

const toList = (v) => (v || '').split(',').map(s => s.trim()).filter(Boolean);

const draftToForm = (d = {}) => ({
  chief_complaint: d.chief_complaint || '',
  symptoms: (d.symptoms || []).join(', '),
  symptom_details: d.symptom_details || '',
  bp_systolic: d.vitals?.blood_pressure?.systolic ?? '',
  bp_diastolic: d.vitals?.blood_pressure?.diastolic ?? '',
  heart_rate: d.vitals?.heart_rate ?? '',
  temperature: d.vitals?.temperature ?? '',
  weight: d.vitals?.weight ?? '',
  medications: (d.medications || []).map(m => [m.name, m.dose, m.frequency].map(x => x || '').join(' | ').replace(/( \| )+$/, '')).join('\n'),
  medication_changes: d.medication_changes || '',
  allergies: (d.allergies || []).join(', '),
  allergy_updates: d.allergy_updates || '',
  additional_notes: d.additional_notes || '',
});

const num = (v) => (v === '' || v === null ? null : Number(v));

const formToDraft = (f) => ({
  chief_complaint: f.chief_complaint || null,
  symptoms: toList(f.symptoms),
  symptom_details: f.symptom_details || null,
  vitals: {
    blood_pressure: f.bp_systolic !== '' ? { systolic: num(f.bp_systolic), diastolic: num(f.bp_diastolic) } : null,
    heart_rate: num(f.heart_rate),
    temperature: num(f.temperature),
    weight: num(f.weight),
  },
  medications: f.medications.split('\n').map(s => s.trim()).filter(Boolean).map(line => {
    const [name, dose, frequency] = line.split('|').map(x => x.trim());
    return { name, dose: dose || null, frequency: frequency || null };
  }),
  medication_changes: f.medication_changes || null,
  allergies: toList(f.allergies),
  allergy_updates: f.allergy_updates || null,
  additional_notes: f.additional_notes || null,
});

const Field = ({ id, label, value, onChange, multiline, type = 'text' }) => (
  <div>
    <Label htmlFor={id} className="text-xs text-slate-600">{label}</Label>
    {multiline ? (
      <Textarea id={id} value={value} onChange={(e) => onChange(e.target.value)} rows={2} data-testid={`review-field-${id}`} />
    ) : (
      <Input id={id} type={type} value={value} onChange={(e) => onChange(e.target.value)} data-testid={`review-field-${id}`} />
    )}
  </div>
);

const Transcript = ({ history }) => (
  <div className="max-h-72 overflow-y-auto space-y-2 bg-slate-50 rounded-lg p-3" data-testid="review-transcript">
    {(history || []).map((m, i) => (
      <p key={i} className={`text-sm ${m.role === 'user' ? 'text-slate-900' : 'text-slate-500'}`}>
        <span className="font-semibold">{m.role === 'user' ? 'Patient' : 'Assistant'}:</span> {m.content}
      </p>
    ))}
  </div>
);

const ReviewEditor = ({ review, onDone }) => {
  const [form, setForm] = useState(draftToForm(review.draft));
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const set = (k) => (v) => setForm({ ...form, [k]: v });

  const decide = async (approve) => {
    try {
      setBusy(true);
      if (approve) {
        const res = await aiCmaAPI.approveReview(review.id, { draft: formToDraft(form), reviewer_notes: notes || null });
        toast.success(res.data.attached_to_intake ? 'Approved and added to the visit intake' : 'Approved and saved to the check-in record');
      } else {
        await aiCmaAPI.rejectReview(review.id, { reviewer_notes: notes || null });
        toast.success('Check-in dismissed');
      }
      onDone();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save review');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card data-testid="review-editor">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">{review.patient_name}</CardTitle>
          <Badge className="bg-amber-100 text-amber-800">Patient-reported, needs review</Badge>
        </div>
        <p className="text-xs text-slate-500">Completed {new Date(review.completed_at).toLocaleString()}</p>
      </CardHeader>
      <CardContent className="space-y-4">
        <Field id="chief_complaint" label="Reason for visit" value={form.chief_complaint} onChange={set('chief_complaint')} />
        <Field id="symptoms" label="Symptoms (comma separated)" value={form.symptoms} onChange={set('symptoms')} />
        <Field id="symptom_details" label="Symptom details" value={form.symptom_details} onChange={set('symptom_details')} multiline />
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Field id="bp_systolic" label="BP systolic" type="number" value={form.bp_systolic} onChange={set('bp_systolic')} />
          <Field id="bp_diastolic" label="BP diastolic" type="number" value={form.bp_diastolic} onChange={set('bp_diastolic')} />
          <Field id="heart_rate" label="Heart rate" type="number" value={form.heart_rate} onChange={set('heart_rate')} />
          <Field id="temperature" label="Temp (°F)" type="number" value={form.temperature} onChange={set('temperature')} />
          <Field id="weight" label="Weight (lbs)" type="number" value={form.weight} onChange={set('weight')} />
        </div>
        <Field id="medications" label="Current medications (one per line: name | dose | how often)" value={form.medications} onChange={set('medications')} multiline />
        <Field id="medication_changes" label="Medication changes" value={form.medication_changes} onChange={set('medication_changes')} multiline />
        <Field id="allergies" label="Allergies (comma separated)" value={form.allergies} onChange={set('allergies')} />
        <Field id="allergy_updates" label="New allergies reported" value={form.allergy_updates} onChange={set('allergy_updates')} />
        <Field id="additional_notes" label="Other notes" value={form.additional_notes} onChange={set('additional_notes')} multiline />
        <Field id="reviewer_notes" label="Your review notes (optional)" value={notes} onChange={setNotes} multiline />

        <Button variant="ghost" size="sm" onClick={() => setShowChat(!showChat)} data-testid="review-toggle-transcript">
          <ChatText size={16} className="mr-1" /> {showChat ? 'Hide' : 'Show'} full conversation
        </Button>
        {showChat && <Transcript history={review.conversation_history} />}

        <div className="flex gap-3 pt-2">
          <Button onClick={() => decide(true)} disabled={busy} className="bg-teal-600 hover:bg-teal-700" data-testid="review-approve-btn">
            {busy ? <SpinnerGap size={16} className="animate-spin mr-2" /> : <CheckCircle size={16} className="mr-2" />} Confirm & Approve
          </Button>
          <Button variant="outline" onClick={() => decide(false)} disabled={busy} className="text-red-600" data-testid="review-reject-btn">
            <XCircle size={16} className="mr-2" /> Dismiss
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

const ApprovedView = ({ review }) => {
  const d = review.approved_data || {};
  const bp = d.vitals?.blood_pressure;
  const rows = [
    ['Reason for visit', d.chief_complaint],
    ['Symptoms', (d.symptoms || []).join(', ')],
    ['Symptom details', d.symptom_details],
    ['Blood pressure', bp?.systolic ? `${bp.systolic}/${bp.diastolic ?? ''}` : null],
    ['Heart rate', d.vitals?.heart_rate],
    ['Temperature', d.vitals?.temperature],
    ['Weight', d.vitals?.weight],
    ['Medications', (d.medications || []).map(m => [m.name, m.dose, m.frequency].filter(Boolean).join(' ')).join('; ')],
    ['Medication changes', d.medication_changes],
    ['Allergies', (d.allergies || []).join(', ')],
    ['New allergies', d.allergy_updates],
    ['Other notes', d.additional_notes],
    ['Review notes', review.reviewer_notes],
  ].filter(([, v]) => v !== null && v !== undefined && v !== '');
  return (
    <Card data-testid="approved-view">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">{review.patient_name}</CardTitle>
          <Badge className="bg-green-100 text-green-800">Verified by {review.reviewed_by_name || 'staff'}</Badge>
        </div>
        <p className="text-xs text-slate-500">Approved {new Date(review.reviewed_at).toLocaleString()} • Patient-reported via AI check-in</p>
      </CardHeader>
      <CardContent>
        <dl className="grid grid-cols-1 md:grid-cols-3 gap-x-4 gap-y-3">
          {rows.map(([k, v]) => (
            <div key={k} className="md:contents">
              <dt className="text-xs font-semibold text-slate-500">{k}</dt>
              <dd className="md:col-span-2 text-sm text-slate-900">{String(v)}</dd>
            </div>
          ))}
        </dl>
      </CardContent>
    </Card>
  );
};

export default function AICheckInReviewsPage() {
  const [reviews, setReviews] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('pending_review');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await aiCmaAPI.getReviews(tab);
      setReviews(res.data.reviews || []);
      setSelected(null);
    } catch (error) {
      toast.error('Failed to load check-ins');
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="ai-checkin-reviews-page">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 bg-teal-100 rounded-lg"><Robot size={28} weight="duotone" className="text-teal-600" /></div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>AI Check-In Reviews</h1>
          <p className="text-slate-500 text-sm">Confirm what patients told the virtual assistant before it goes into the visit record</p>
        </div>
      </div>
      <div className="flex gap-2 mb-6">
        <Button variant={tab === 'pending_review' ? 'default' : 'outline'} className={tab === 'pending_review' ? 'bg-teal-600' : ''} onClick={() => setTab('pending_review')} data-testid="reviews-tab-pending">Waiting for review</Button>
        <Button variant={tab === 'approved' ? 'default' : 'outline'} className={tab === 'approved' ? 'bg-teal-600' : ''} onClick={() => setTab('approved')} data-testid="reviews-tab-approved">Approved</Button>
      </div>
      {loading ? (
        <div className="flex justify-center py-12"><SpinnerGap size={32} className="animate-spin text-teal-600" /></div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card className="lg:col-span-1">
            <CardHeader className="pb-3"><CardTitle className="text-lg">{tab === 'approved' ? 'Approved' : 'Waiting for review'} ({reviews.length})</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {reviews.length === 0 && <p className="text-slate-500 text-sm text-center py-4" data-testid="reviews-empty">{tab === 'approved' ? 'No approved check-ins yet.' : 'No check-ins waiting.'}</p>}
              {reviews.map(r => (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => setSelected(r)}
                  data-testid={`review-item-${r.id}`}
                  className={`w-full text-left p-3 rounded-lg border transition-colors ${selected?.id === r.id ? 'bg-teal-50 border-teal-200' : 'border-transparent hover:bg-slate-50'}`}
                >
                  <div className="flex items-center gap-3">
                    <User size={20} className="text-slate-500" />
                    <div>
                      <p className="font-medium">{r.patient_name}</p>
                      <p className="text-xs text-slate-500">{r.draft?.chief_complaint || 'No reason given'}</p>
                    </div>
                  </div>
                </button>
              ))}
            </CardContent>
          </Card>
          <div className="lg:col-span-2">
            {selected ? (
              tab === 'approved'
                ? <ApprovedView review={selected} />
                : <ReviewEditor key={selected.id} review={selected} onDone={load} />
            ) : (
              <Card><CardContent className="py-12 text-center text-slate-500">Select a check-in to review</CardContent></Card>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
